import { useEffect, useRef } from 'react'
import { APP_NAME, GUEST_REEL_CAP, IG_BOT_HANDLE } from '@verso/shared/src/constants/app'
import { DownloadCta } from '../components/DownloadCta'
import { StickyDownloadBar } from '../components/StickyDownloadBar'
import { trackWeb, WEB_EVENTS } from '../lib/analytics'
import './get.css'

/**
 * The hero CTA anchor. Its own id (not the landing's `#get`) so the two pages
 * never both claim one global id; <StickyDownloadBar> is told which to watch.
 */
const HERO_CTA_ID = 'gt-cta'

/**
 * Section-progression funnel. Same event as the landing so the two pages sit in
 * one PostHog funnel, distinguished by the `page` property and by the `get_*`
 * section names.
 */
const TRACKED_SECTIONS: Array<[string, string]> = [
  ['.gthero', 'get_hero'],
  // A new section VALUE, not a new event name - `get_proof` already belongs to
  // "The real thing" further down, so the story panels need their own. The
  // observer below silently skips a selector it cannot find, so a typo here
  // degrades to a missing funnel step with no error anywhere.
  ['.gtstory-sec', 'get_story'],
  ['.gtsteps-sec', 'get_steps'],
  ['.gtproof-sec', 'get_proof'],
  ['.gtfaq-sec', 'get_faq'],
  ['.gtfinal', 'get_final'],
]

/**
 * `/get` metadata. The SPA ships ONE index.html and its head is written for `/`,
 * so without this the browser tab contradicts the ad that sent the visitor here,
 * a shared `/get` link unfurls as the landing page, and `og:url` points every
 * scraper back at `/` - an implicit canonical away from the page the ad spend is
 * buying. Applied as an effect and UNDONE on unmount, so a client-side
 * navigation back to `/` is not left carrying this page's head.
 *
 * A non-JS scraper still sees index.html's `/` head; closing that needs a Pages
 * Function or a prerender, not a bigger effect.
 */
const PAGE_TITLE = `${APP_NAME} - save the places in your travel reels`
const PAGE_DESCRIPTION =
  `${APP_NAME} finds places in your travel reels and keeps them in collections. Free to download on iOS and Android.`
const PAGE_URL = 'https://heycompass.app/get'

/**
 * Candidate selection for the two story images, which are BELOW the fold and
 * lazily fetched. There is no longer any preload to stay byte-identical with:
 * the `/get` branch of index.html's route-aware block used to preload both of
 * these at `fetchpriority=high` back when they sat in the hero, and that link
 * pair was deleted when the story moved down. Nothing mirrors these strings any
 * more - they only tell the browser which candidate to pick.
 *
 * They live on the `<source>` only: the `<img>` fallbacks carry no `srcset`,
 * which makes `sizes` inert there. Both are spelled out as media queries rather
 * than as `min()` because a `sizes` attribute is only guaranteed to accept
 * plain lengths.
 *
 * REEL_SIZES tracks the CSS `--reelw`. PLAN_SIZES is the rendered width of the
 * phone SCREEN, not of the device: the panel column (or `--phonew`, whichever
 * is smaller) minus twice the bezel.
 */
const REEL_SIZES =
  '(min-width: 1060px) 176px, (min-width: 760px) 150px, (min-width: 400px) 140px, 132px'
const PLAN_SIZES =
  '(min-width: 1060px) 280px, (min-width: 760px) 180px, (min-width: 400px) 288px, 248px'

/**
 * `/get` - the conversion page for Meta paid traffic (Indian mobile, mostly
 * Android, arriving inside the Instagram in-app webview). It competes with `/`,
 * whose broader story uses the same supported save-to-plan promise.
 *
 * Four constraints drive every decision here:
 *  1. Fold one is ONE action. The headline, the sub, the download button, the
 *     trust row and the teaser line pointing at the story below - and no image
 *     at all - on a 360x640 Android minus the 58px /app-banner.js strip. The
 *     button closes above 356px. The story visual lives in the section below,
 *     so it no longer competes with the button for the first screen.
 *  2. Message match: the ads promise "send a reel, get a trip", so the headline
 *     says that and nothing else.
 *  3. Readability is a conversion mechanic, not a nicety. Nothing under 13px,
 *     body copy at 16px, every text role clear of 4.5:1 on its background.
 *     Those are RENDERED pixels: the root font-size is 15px, so the rem
 *     literals in get.css are 16/15 multiples and none of them reads as its
 *     own pixel value. The contract block at the top of that file is the
 *     authority - do not "correct" a size by its literal.
 *  4. Only claims that are true of the shipped app. No counts, no ratings, no
 *     testimonials, no press - none of that exists, and inventing it would be
 *     worse than the objection it is meant to answer.
 */
export default function Get() {
  const rootRef = useRef<HTMLDivElement>(null)

  // Route-scoped <head>. Every write records its own undo, and the cleanup
  // replays them in reverse: a tag this page CREATED is removed again, a tag it
  // merely overwrote goes back to the value index.html shipped.
  useEffect(() => {
    const undo: Array<() => void> = []

    const previousTitle = document.title
    document.title = PAGE_TITLE
    undo.push(() => {
      document.title = previousTitle
    })

    const setMeta = (attr: 'name' | 'property', key: string, content: string) => {
      const existing = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`)
      if (existing) {
        const previous = existing.getAttribute('content')
        existing.setAttribute('content', content)
        undo.push(() => {
          if (previous === null) existing.removeAttribute('content')
          else existing.setAttribute('content', previous)
        })
        return
      }
      const created = document.createElement('meta')
      created.setAttribute(attr, key)
      created.setAttribute('content', content)
      document.head.appendChild(created)
      undo.push(() => created.remove())
    }

    setMeta('name', 'description', PAGE_DESCRIPTION)
    setMeta('property', 'og:title', PAGE_TITLE)
    setMeta('property', 'og:description', PAGE_DESCRIPTION)
    setMeta('property', 'og:url', PAGE_URL)
    // index.html carries `/`-specific twitter:* tags, so they do NOT fall back
    // to the og:* pair above - a /get link shared on X would unfurl as `/`.
    setMeta('name', 'twitter:title', PAGE_TITLE)
    setMeta('name', 'twitter:description', PAGE_DESCRIPTION)

    const existingCanonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
    if (existingCanonical) {
      const previous = existingCanonical.getAttribute('href')
      existingCanonical.setAttribute('href', PAGE_URL)
      undo.push(() => {
        if (previous === null) existingCanonical.removeAttribute('href')
        else existingCanonical.setAttribute('href', previous)
      })
    } else {
      const created = document.createElement('link')
      created.setAttribute('rel', 'canonical')
      created.setAttribute('href', PAGE_URL)
      document.head.appendChild(created)
      undo.push(() => created.remove())
    }

    return () => {
      undo.reverse().forEach((fn) => fn())
    }
  }, [])

  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    if (!('IntersectionObserver' in window)) return
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return
          const name = entry.target.getAttribute('data-sv')
          if (name) trackWeb(WEB_EVENTS.WEB_LANDING_SECTION_VIEWED, { section: name, page: 'get' })
          io.unobserve(entry.target)
        })
      },
      { threshold: 0, rootMargin: '0px 0px -20% 0px' },
    )
    TRACKED_SECTIONS.forEach(([selector, name]) => {
      const el = root.querySelector(selector)
      if (!el) return
      el.setAttribute('data-sv', name)
      io.observe(el)
    })
    return () => io.disconnect()
  }, [])

  return (
    <div className='gt' ref={rootRef}>
      <div className='gtwrap'>
        {/* Wordmark only. "Free on iOS and Android" used to sit here too, but the
            trust row repeats it a few hundred pixels below, directly under the
            CTA where it actually answers the objection - saying it twice in one
            fold just crowded the masthead. */}
        <header className='gttop'>
          <span className='gtmark'>{APP_NAME}</span>
        </header>

        {/* ============== HERO ============== */}
        <section className='gthero'>
          <p className='gteyebrow'>Instagram and YouTube</p>
          <h1>
            Save the reel.
            <br />
            <span className='gtaccent'>Keep the places.</span>
          </h1>
          <p className='gtsub'>
            Find places in your travel reels. Keep them for later.
          </p>
          <div className='gtstores' id={HERO_CTA_ID}>
            <DownloadCta placement='get_hero' />
          </div>
          <ul className='gttrust'>
            <li>Free to download</li>
            <li>iOS and Android</li>
            <li>Reels and Shorts</li>
          </ul>

          {/* The whole point of the bare fold: one line that says evidence is
              coming, so the screen can be one button without reading as thin.
              It sits BELOW the CTA, so it cannot push the button down. */}
          <p className='gtteaser'>
            See how a reel becomes places you can return to.
            <span className='gtteasercue' aria-hidden='true'>
              <svg
                width='15'
                height='15'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              >
                <path d='M12 5v14' />
                <path d='M6 13l6 6 6-6' />
              </svg>
            </span>
          </p>
        </section>

        {/* ============== THE STORY ============== */}
        {/* Source reel, extraction explanation and real saved-place capture. */}
        <section className='gtsec gtstory-sec'>
          <p className='gtkicker'>From reel to saved places</p>
          <h2>A place to start your next trip.</h2>

          <div className='gtstory'>
            {/* Cropped 9:16 out of the SAME screenshot as the plan below (its
                Gulmarg hero band), so the reel that goes in and the plan that
                comes back are one story rather than a beach reel answered by a
                snow trip. */}
            <figure className='gtpanel'>
              <figcaption className='gtpanelcap'>You send a reel</figcaption>
              <div className='gtreel'>
                <picture>
                  <source
                    type='image/webp'
                    srcSet='/proof/reel-snow-264.webp 264w'
                    sizes={REEL_SIZES}
                  />
                  <img
                    src='/proof/reel-snow-264.jpg'
                    width={264}
                    height={468}
                    alt=''
                    loading='lazy'
                    decoding='async'
                  />
                </picture>
                <span className='gtplay' aria-hidden='true' />
                {/* A corner label with a text-shadow, not a full-width pill: at
                    the narrowest rendered reel the pill wrapped to two lines. */}
                <span className='gtreellabel'>Your reel</span>
              </div>
            </figure>

            <div className='gtflow' aria-hidden='true'>
              <span className='gtflowglyph'>
                <svg
                  width='20'
                  height='20'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                >
                  <path d='M4 12h15' />
                  <path d='M13 6l6 6-6 6' />
                </svg>
              </span>
            </div>

            {/* Explain the mechanism without inventing a bot conversation. */}
            <figure className='gtpanel'>
              <figcaption className='gtpanelcap'>Find the places</figcaption>
              <div className='gtbot gtmechanism'>
                <p className='gtkicker'>How it works</p>
                <h3>From inspiration to somewhere you can find.</h3>
                <p>{APP_NAME} looks for the places in your reel and saves what it can identify.</p>
                <p>Review the results. Uncertain matches are marked for you to check.</p>
              </div>
            </figure>

            <div className='gtflow' aria-hidden='true'>
              <span className='gtflowglyph'>
                <svg
                  width='20'
                  height='20'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                >
                  <path d='M4 12h15' />
                  <path d='M13 6l6 6-6 6' />
                </svg>
              </span>
            </div>

            {/* captured: tools/store-screenshots/raw/itinerary.png, cropped to
                (0, 1116, 1206, 2305) - below the creator-handle meta line and
                above the compass FAB. A real capture on current tokens, not a
                DOM recreation: the 72pt photo thumbnail on every day row is the
                whole point, and there is no licensed photography in the repo to
                rebuild it from. Re-crop by shipping a NEW filename: /proof/* is
                served immutable. */}
            <figure className='gtpanel'>
              <figcaption className='gtpanelcap'>Places from one reel</figcaption>
              <div className='gtphone'>
                <div className='gtscreen'>
                  <picture>
                    <source
                      type='image/webp'
                      srcSet='/proof/plan-320.webp 320w, /proof/plan-640.webp 640w'
                      sizes={PLAN_SIZES}
                    />
                    <img
                      src='/proof/plan-320.jpg'
                      width={320}
                      height={315}
                      alt='A trip in Compass, day by day: Gulmarg, Pahalgam and Manali, each day carrying its own photo.'
                      loading='lazy'
                      decoding='async'
                    />
                  </picture>
                </div>
              </div>
            </figure>
          </div>
        </section>

        {/* ============== HOW IT WORKS ============== */}
        {/* Keep the app instructions distinct from the Instagram entry path. */}
        <section className='gtsec gtsteps-sec'>
          <p className='gtkicker'>How it works</p>
          <h2>Start with one travel reel.</h2>

          <ol className='gtsteps'>
            <li className='gtstep'>
              <span className='gtnum'>01</span>
              <h3>Get the app</h3>
              <p>
                Download {APP_NAME} on iOS or Android and sign in. Have a travel reel you want to keep ready.
              </p>
            </li>

            <li className='gtstep'>
              <span className='gtnum'>02</span>
              <h3>Share a travel link</h3>
              <p>
                Share from Instagram or YouTube into {APP_NAME}. You can also paste the link in the app.
              </p>
            </li>

            <li className='gtstep'>
              <span className='gtnum'>03</span>
              <h3>Keep the places</h3>
              <p>
                Find the identified places in your collections. Build a trip from your saves when you are ready.
              </p>
            </li>
          </ol>

          <div className='gtctablock'>
            <p className='gtctaline'>Give your next travel save a home.</p>
            <div className='gtstores'>
              <DownloadCta placement='get_steps' />
            </div>
            <p className='gtalternative'>
              Prefer to try it in Instagram?{' '}
              <a href={`https://ig.me/m/${IG_BOT_HANDLE}`} onClick={() => trackWeb(WEB_EVENTS.WEB_INSTAGRAM_CTA_CLICKED, { placement: 'get_steps' })}>
                Send a travel reel to @{IG_BOT_HANDLE}
              </a>. Your first {GUEST_REEL_CAP} guest reels do not need an app account.
            </p>
          </div>
        </section>

        {/* ============== WHAT YOU END UP WITH ============== */}
        {/* No inline CTA in this section or the next: the sticky bar is already
            on screen by here, and a fifth competing button costs more than it
            earns. */}
        <section className='gtsec gtproof-sec'>
          <p className='gtkicker'>The real thing</p>
          <h2>Your saves, sorted by where they are.</h2>

          <div className='gtshot'>
            <picture>
              <source
                type='image/webp'
                srcSet='/proof/board-488.webp 488w, /proof/board-760.webp 760w'
                sizes='(min-width: 760px) 760px, 100vw'
              />
              <img
                src='/proof/board-488.jpg'
                width={488}
                height={405}
                alt='The Compass home screen, showing saved places grouped into boards by country, with India split by state.'
                loading='lazy'
                decoding='async'
              />
            </picture>
          </div>
          <p className='gtshotcap'>
            A real {APP_NAME} screen. Collections group places by country, and India by state.
          </p>

          <div className='gtclaims'>
            <div className='gtclaim'>
              <h3>A plan, not a top-10 list</h3>
              <p>
                {APP_NAME} can turn the places you kept into a day-by-day trip, then leaves the
                plan open to edit.
              </p>
            </div>
            <div className='gtclaim'>
              <h3>The cost, before you commit</h3>
              {/* "the flight", not "the return flight from India": a domestic
                  India trip - which FAQ 4 actively promotes - is priced through
                  `estimateDomesticTransport`, not `flightFromIndiaInr`. */}
              <p>
                Every trip carries a per-person estimate: the flight plus what the days cost on the
                ground.
              </p>
            </div>
          </div>

        </section>

        {/* ============== QUESTIONS ============== */}
        <section className='gtsec gtfaq-sec'>
          <p className='gtkicker'>Before you tap</p>
          <h2>The questions people actually ask.</h2>

          <div className='gtfaq'>
            <details className='gtq'>
              <summary>
                Is it free?
                <span className='gtplus' aria-hidden='true' />
              </summary>
              <p className='gta'>
                {APP_NAME} is free to download on the App Store and Google Play. You can also try up to {GUEST_REEL_CAP} travel reels in Instagram before creating an app account.
              </p>
            </details>

            <details className='gtq'>
              <summary>
                Do I need an account?
                <span className='gtplus' aria-hidden='true' />
              </summary>
              {/* NO claim about what is or is not stored. The earlier "keeps
                  your name and photo, nothing else" was false - `users` holds
                  email, avatar, phone, instagram handle, preferences, upi id,
                  push token and taste profile (packages/db/src/schema/users.ts,
                  written by workers/api/src/routes/webhooks.routes.ts) - and
                  privacy.html, linked from this page's own footer, says so. */}
              <p className='gta'>
                The app needs sign-in with Google or Apple. To try it first, send up to {GUEST_REEL_CAP} travel reels to @{IG_BOT_HANDLE} on Instagram without an app account.
              </p>
            </details>

            <details className='gtq'>
              <summary>
                What can I send it?
                <span className='gtplus' aria-hidden='true' />
              </summary>
              <p className='gta'>
                Instagram reels and carousels, plus YouTube videos and Shorts, can be shared into {APP_NAME}. Instagram content can also be sent to @{IG_BOT_HANDLE} in Instagram DMs. YouTube links go through the app, not the Instagram bot.
              </p>
            </details>

            <details className='gtq'>
              <summary>
                Does it work for trips inside India?
                <span className='gtplus' aria-hidden='true' />
              </summary>
              <p className='gta'>
                Yes. Boards group by country, and India splits by state, so Goa, Himachal and
                Meghalaya each get their own board.
              </p>
            </details>

            <details className='gtq'>
              <summary>
                Can I plan with other people?
                <span className='gtplus' aria-hidden='true' />
              </summary>
              <p className='gta'>
                Yes. Share a trip and everyone's saves land on one board, and you can see who added
                what.
              </p>
            </details>

            <details className='gtq'>
              <summary>
                Does it book flights and hotels?
                <span className='gtplus' aria-hidden='true' />
              </summary>
              <p className='gta'>
                Saving a reel does not make a booking. Use your saved places to plan, then review availability and current prices before booking anything.
              </p>
            </details>
          </div>
        </section>

        {/* ============== CLOSE ============== */}
        <section className='gtfinal'>
          <h2>
            Your next trip starts with <span className='gtaccent'>one reel</span>.
          </h2>
          <div className='gtstores'>
            <DownloadCta placement='get_final' />
          </div>
          <p className='gtfine'>Free to download on iOS and Android.</p>
        </section>

        <footer className='gtpagefoot'>
          <span className='gtmark'>{APP_NAME}</span>
          <span className='gtlegal'>(c) 2026 {APP_NAME}, India</span>
          <span className='gtlinks'>
            <a href='/privacy.html'>Privacy</a>
            <a href='/terms.html'>Terms</a>
            <a href='/support.html'>Support</a>
          </span>
        </footer>
      </div>

      <StickyDownloadBar heroCtaId={HERO_CTA_ID} />
    </div>
  )
}
